import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { ProductCard } from '../ProductCard';
import { tileGridStyle } from '../../utils/gridStyles';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import type { WidgetProps } from './types';
import type { Product } from '../../utils/types';
import { getApiBase } from '../../utils/api';

async function fetchPromotedProducts(lang?: string): Promise<Product[]> {
  try {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`${getApiBase()}/promoted-products${qs}`, {
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return [];
    return (await res.json()) as Product[];
  } catch {
    return [];
  }
}

export const PromotedWinesWidget: React.FC<WidgetProps> = ({ config, defaults, onProductClick, itemsPerRow }) => {
  const { heading } = useFontSizes();
  const { t, lang } = useI18n();
  const [promotedProducts, setPromotedProducts] = useState<Product[] | null>(null);

  useEffect(() => {
    fetchPromotedProducts(lang).then((data) => setPromotedProducts(data));
  }, [lang]);

  // Use promoted products if available, fall back to defaults.promos
  const source = promotedProducts !== null && promotedProducts.length > 0
    ? promotedProducts
    : (defaults?.promos ?? []);

  const items = config.maxItems ? source.slice(0, config.maxItems) : source.slice(0, 8);
  if (items.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#f02e69] mb-4`}>
        <Sparkles className="h-4 w-4" />
        {config.title ?? t('defaults.promos')}
      </h2>
      <div style={tileGridStyle('medium', itemsPerRow ?? 4)}>
        {items.map((product, index) => (
          <div
            key={product.id}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 40}ms` }}
          >
            <ProductCard product={product} onProductClick={onProductClick} />
          </div>
        ))}
      </div>
    </section>
  );
};
